import { HearthBeatLoader } from '@components/ui';
import connectionService from '@services/internal/connection.service';
import EnvService from '@services/internal/env';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { addContact, sendDirectAttachment, sendDirectMessage } from '../../helpers/strophe-helpers';
import { getDateFormat, groupMessagesByDate } from '../../helpers/utils-helpers';
import { actions } from '../../store/ducks/direct-chat';
import { TAttachment } from '../../types/attachments';
import { TDirectMessage } from '../../types/direct-chat';
import TStore from '../../types/store';
import { TCurrentUser, TUserPreview } from '../../types/users';
import ChatBubble from '../chat-bubble';
import ChatInput from './chat-input';
import { ChatGroupTitleSC, ChatWindowSC } from './chat-window.styled';

type TProps = {
  searchQuery: string;
  isLoading: boolean;
  currentUser: TCurrentUser | null;
  messages: TDirectMessage[];
  receiver: string;
  searchHistory: TUserPreview[];
  addDirectMessage(message: TDirectMessage): void;
};

/**
 * Returns sender nickname
 *
 * @param receiverDetail an object of receiver
 * @param receiverMessageJid the JID of the sender of the message (can be someone else as the receiver)
 * @param isFromCurrentUser does render nothing if current user
 * @return string sender nickname
 * @author jozef.repan
 */
const resolveSenderNickname = (
  receiverDetail: any,
  receiverMessageJid: string,
  isFromCurrentUser: boolean,
): string => {
  // don't get this but ok
  if (!receiverDetail || isFromCurrentUser) {
    return '';
  }

  // check if not Hilbi
  if (receiverDetail.jabberId === EnvService.getSnapshot().xmpp.hilbiJid) {
    return 'Hilbi';
  }
  // otherwise return receiver name
  return `${receiverDetail.profile.firstName} ${receiverDetail.profile.lastName}`;
};

const ChatDirectWindow: React.FC<TProps> = ({
  searchQuery,
  currentUser,
  messages,
  receiver,
  searchHistory,
  isLoading,
  addDirectMessage,
}: TProps) => {
  const scrollView: any = useRef(null);
  const scrollToBottom = () => {
    scrollView.current.scrollTop = scrollView.current.scrollHeight;
  };

  useEffect(() => {
    if (isLoading === false && messages) {
      const scrollHeight =
        scrollView.current.scrollHeight === null ? 0 : scrollView.current.scrollHeight;
      setTimeout(() => {
        if (scrollView.current) {
          scrollView.current.scrollTop = scrollHeight;
        }
      }, 500);
    }
  }, [isLoading, searchQuery]);

  const isSequence = (
    previousMessage: TDirectMessage | null,
    currentMessage: TDirectMessage,
  ): boolean => {
    if (previousMessage === null) {
      return false;
    }
    return previousMessage.from === currentMessage.from;
  };

  const renderGroups = () => {
    return groupMessagesByDate(messages).map((group: any) => {
      const el = group.messages.find((a: any) => (a.body.includes(searchQuery) ? true : false));
      if (!el) {
        return null;
      }
      return (
        <div key={group.date}>
          <ChatGroupTitleSC>
            <span>{moment(group.date).format(getDateFormat())}</span>
          </ChatGroupTitleSC>
          {renderMessages(group.messages)}
        </div>
      );
    });
  };

  const renderMessages = (messageItems: TDirectMessage[]) => {
    let previousMessage: TDirectMessage | null = null;

    const receiverDetail = searchHistory.find((user) => user.jabberId === receiver);
    // TODO:SENDERHILBIFIX
    return messageItems.map((message, index) => {
      const sequence = isSequence(previousMessage, message);
      previousMessage = message;
      const isFromCurrentUser =
        message.from === 'me' || message.from === '' || message.from === currentUser?.jabberId;
      const nickname = resolveSenderNickname(receiverDetail, message.to, isFromCurrentUser);

      let avatarUrl = '';

      if (isFromCurrentUser) {
        avatarUrl = currentUser?.profile.profileImage || '';
      } else {
        avatarUrl = receiverDetail?.profile.profileImage || '';
      }
      const messageBody = `${message.body}`;
      if (searchQuery && !messageBody.toLowerCase().includes(searchQuery.toLowerCase())) {
        return null;
      }

      return (
        <ChatBubble
          stamp={message.stamp}
          avatarUrl={avatarUrl}
          text={message.body}
          from={nickname}
          isMe={isFromCurrentUser}
          key={index}
          isSequence={sequence}
          alertVital={message.alertVital}
          styleType={message.alertStyle}
          attachment={message.attachment}
        />
      );
    });
  };

  const onMessageSubmit = async (message: string, attachment?: TAttachment) => {
    if (message.trim() === '' && !attachment) {
      return;
    }

    const id = (await connectionService.waitForConnection()).getUniqueId();

    addDirectMessage({
      from: 'me',
      to: receiver,
      body: message,
      uuid: id,
      thread: receiver,
      stamp: moment().format(),
      attachment,
    });
    if (attachment) {
      await sendDirectAttachment(message, attachment, receiver, id);
    } else {
      await sendDirectMessage(message, receiver, id);
    }
  };

  useEffect(scrollToBottom, [isLoading]);
  useEffect(scrollToBottom, [messages]);

  // send subscribe request
  useEffect(() => {
    addContact(receiver, receiver);
  }, [receiver]);

  return (
    <ChatWindowSC>
      <div className="chw-chat">
        <div className="chw-scrollable" ref={scrollView}>
          {isLoading ? null : renderGroups()}
        </div>
        {isLoading ? (
          <div className="chw-loading">
            <HearthBeatLoader loading size="medium" />
            {/* <img src={require('../../assets/gif/hilbi-loader.gif').default} alt="Loading" /> */}
          </div>
        ) : null}
      </div>
      <div className="chw-bottom-bar">
        <ChatInput onSubmit={onMessageSubmit} />
      </div>
    </ChatWindowSC>
  );
};

const mapStateToProps = (store: TStore) => ({
  currentUser: store.users.currentUser,
  messages: store.directChat.messages,
  receiver: store.directChat.receiver,
  isLoading: store.directChat.isLoading,
  searchHistory: store.users.searchHistory,
});

const mapDispatchToProps = (dispatch: any) => ({
  addDirectMessage: (message: TDirectMessage) => dispatch(actions.addDirectMessage(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatDirectWindow);
